import { useCallback, useEffect, useState } from "react";
import {
  ArrowLeft, CheckCircle2, Circle, Lock, PlayCircle, Award, ExternalLink, BookOpen,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { API } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TIER_LABEL = { free: "Free", starter: "Starter", growth: "Growth", enterprise: "Enterprise" };

export function AcademyPath({ slug, onBack, onOpenCourse }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/lms/paths/${slug}`).then(({ data }) => setD(data)).catch(() => toast.error("Could not load path")), [slug]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-[#71717A]">Loading path…</p>;

  const downloadPathCert = () => { window.open(`${API}/lms/paths/${d.path_id}/certificate.pdf`, "_blank"); toast.success("Downloading your learning-path certificate"); };

  return (
    <div className="fv-fade-up" data-testid="academy-path">
      <button onClick={onBack} data-testid="path-back-btn" className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back to Academy</button>
      <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 mb-6">
        <div className="text-[10px] uppercase tracking-wider text-[#D4AF37] mb-1">Learning Path · {TIER_LABEL[d.tier]}</div>
        <h1 className="font-heading text-3xl text-white mb-2">{d.title}</h1>
        <p className="text-[#A1A1AA] text-sm mb-4 max-w-2xl">{d.description}</p>
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md"><Progress value={d.progress} className="h-2 bg-white/5" /></div>
          <span className="text-[#D4AF37] text-sm font-medium">{d.progress}%</span>
          {d.path_complete && <Button onClick={downloadPathCert} data-testid="path-certificate-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full"><Award className="w-4 h-4 mr-1" /> Path certificate</Button>}
        </div>
      </div>

      <div className="space-y-3" data-testid="path-courses">
        {d.courses.map((c) => (
          <button key={c.course_id} onClick={() => !c.locked && onOpenCourse(c.slug)} disabled={c.locked} data-testid={`path-course-${c.slug}`}
            className={`w-full text-left rounded-2xl border p-5 flex items-center gap-4 transition-colors ${c.locked ? "border-white/5 bg-[#0d0d0f] cursor-not-allowed" : "border-white/8 bg-[#121214] hover:border-[#D4AF37]/40"}`}>
            <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0">
              {c.locked ? <Lock className="w-5 h-5 text-[#71717A]" /> : c.completed ? <Award className="w-5 h-5 text-[#D4AF37]" /> : <BookOpen className="w-5 h-5 text-[#D4AF37]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading text-lg text-white">{c.title}</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/8 text-[#A1A1AA]">{c.level}</span>
                {c.locked && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37]">{TIER_LABEL[c.tier]} plan</span>}
              </div>
              <p className="text-sm text-[#71717A] mt-0.5 truncate">{c.description}</p>
              <div className="flex items-center gap-3 mt-2">
                <div className="flex-1 max-w-xs"><Progress value={c.progress} className="h-1.5 bg-white/5" /></div>
                <span className="text-xs text-[#A1A1AA]">{c.lessons_done}/{c.lessons_total} lessons</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function AcademyCourse({ slug, onBack, onOpenLesson, onOpenWorkflow, refreshKey }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/lms/courses/${slug}`).then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load course")), [slug]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!d) return <p className="text-[#71717A]">Loading course…</p>;

  const downloadCert = () => { window.open(`${API}/lms/courses/${d.course_id}/certificate.pdf`, "_blank"); toast.success("Downloading your certificate"); };

  return (
    <div className="fv-fade-up" data-testid="academy-course">
      <button onClick={onBack} data-testid="course-back-btn" className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">{d.path_title} · {d.level}</div>
          <h1 className="font-heading text-3xl text-white mb-2">{d.title}</h1>
          <p className="text-[#A1A1AA] text-sm mb-4">{d.description}</p>

          <div className="rounded-xl border border-white/8 bg-[#121214] p-4 mb-5 flex items-center gap-4">
            <div className="flex-1"><Progress value={d.progress} className="h-2 bg-white/5" /></div>
            <span className="text-[#D4AF37] text-sm font-medium" data-testid="course-progress">{d.progress}%</span>
            {d.resume_lesson_id && !d.completed && <Button onClick={() => onOpenLesson(d.resume_lesson_id)} data-testid="course-resume-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full"><PlayCircle className="w-4 h-4 mr-1" /> Resume</Button>}
          </div>

          <div className="space-y-2" data-testid="course-lessons">
            {d.lessons.map((l) => (
              <button key={l.lesson_id} onClick={() => !l.locked && onOpenLesson(l.lesson_id)} disabled={l.locked} data-testid={`course-lesson-${l.lesson_id}`}
                className={`w-full text-left rounded-xl border p-4 flex items-center gap-3 transition-colors ${l.locked ? "border-white/5 bg-[#0d0d0f] cursor-not-allowed" : "border-white/8 bg-[#121214] hover:border-[#D4AF37]/40"}`}>
                {l.locked ? <Lock className="w-4 h-4 text-[#52525B] shrink-0" /> : l.completed ? <CheckCircle2 className="w-4 h-4 text-[#D4AF37] shrink-0" /> : <Circle className="w-4 h-4 text-[#52525B] shrink-0" />}
                <span className="text-[10px] text-[#71717A] w-5">{l.order + 1}</span>
                <span className={`text-sm flex-1 ${l.completed ? "text-[#A1A1AA]" : "text-white"}`}>{l.title}</span>
                {l.type === "video" && <PlayCircle className="w-3.5 h-3.5 text-[#71717A]" />}
                <span className="text-[11px] text-[#71717A]">{l.minutes} min</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {d.completed && (
            <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#151310] to-[#121214] p-5 text-center" data-testid="course-certificate-card">
              <Award className="w-10 h-10 text-[#D4AF37] mx-auto mb-2" />
              <div className="font-heading text-lg text-white mb-1">Course complete!</div>
              <p className="text-xs text-[#A1A1AA] mb-3">Download your certificate — it's saved to your Document Library.</p>
              <Button onClick={downloadCert} data-testid="course-certificate-btn" className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">Download certificate</Button>
            </div>
          )}
          {d.workflow && (
            <button onClick={() => onOpenWorkflow(d.workflow.path)} data-testid="course-workflow-cta"
              className="w-full flex items-center justify-between rounded-2xl border border-white/8 bg-[#121214] p-4 hover:border-[#D4AF37]/40 transition-colors">
              <span className="flex items-center gap-2 text-white text-sm font-medium"><PlayCircle className="w-4 h-4 text-[#D4AF37]" /> {d.workflow.label}</span>
              <ExternalLink className="w-4 h-4 text-[#D4AF37]" />
            </button>
          )}
          <div className="rounded-2xl border border-white/8 bg-[#121214] p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">Instructor</div>
            <div className="text-sm text-white">{d.instructor}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#71717A] mt-3 mb-1">Estimated time</div>
            <div className="text-sm text-white">{d.estimated_minutes} min</div>
          </div>
        </div>
      </div>
    </div>
  );
}
